import React from 'react';
import { MessageSquare, Users, Globe2, ArrowRight } from 'lucide-react';

function useInView(ref: React.RefObject<HTMLElement | null>) {
  const [isInView, setIsInView] = React.useState(false);
  React.useEffect(() => {
    if (!ref.current) return;
    const observer = new IntersectionObserver(
      ([entry]) => { if (entry.isIntersecting) setIsInView(true); },
      { threshold: 0.15 }
    );
    observer.observe(ref.current);
    return () => observer.disconnect();
  }, [ref]);
  return isInView;
}

export function MessageCenterPromo() {
  const sectionRef = React.useRef<HTMLElement>(null);
  const isInView = useInView(sectionRef);

  const scrollToTop = () => {
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  return (
    <section ref={sectionRef} className="py-16 sm:py-24 bg-white dark:bg-gray-800">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className={`relative overflow-hidden rounded-3xl bg-gradient-to-br from-primary-50 via-white to-accent-50 dark:from-gray-800 dark:via-gray-800 dark:to-gray-800 border border-primary-100/50 dark:border-gray-700 transition-all duration-700 ${isInView ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'}`}>
          <div className="absolute top-0 right-0 w-[500px] h-[500px] bg-gradient-to-br from-primary-100/30 via-accent-100/20 to-transparent dark:from-primary-800/10 dark:via-accent-800/5 rounded-full transform translate-x-1/2 -translate-y-1/2 blur-3xl pointer-events-none" />

          <div className="grid lg:grid-cols-2 gap-8 lg:gap-0">
            <div className="relative p-8 sm:p-12 lg:p-16">
              <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-primary-100 dark:bg-primary-900/30 text-primary-600 dark:text-primary-400 text-sm font-medium mb-6">
                <MessageSquare className="w-4 h-4" />
                <span>Centre de messages</span>
              </div>

              <h2 className="text-3xl sm:text-4xl font-bold text-gray-900 dark:text-white mb-5">
                Un espace d'&eacute;change<br />
                <span className="text-transparent bg-clip-text bg-gradient-to-r from-primary-500 to-accent-500">100% Pvtistes</span>
              </h2>

              <p className="text-lg text-gray-600 dark:text-gray-400 mb-8 max-w-lg leading-relaxed">
                Posez vos questions, partagez votre exp&eacute;rience et trouvez des r&eacute;ponses utiles gr&acirc;ce &agrave; notre communaut&eacute; de voyageurs.
              </p>

              <div className="grid sm:grid-cols-2 gap-4 mb-8">
                <div className="flex items-center gap-3 p-4 bg-white dark:bg-gray-700/50 rounded-xl border border-gray-100 dark:border-gray-600/50">
                  <div className="w-10 h-10 rounded-xl bg-primary-100 dark:bg-primary-900/30 flex items-center justify-center flex-shrink-0">
                    <Users className="w-5 h-5 text-primary-600 dark:text-primary-400" />
                  </div>
                  <div>
                    <h3 className="font-medium text-gray-900 dark:text-white text-sm">Entraide</h3>
                    <p className="text-xs text-gray-500 dark:text-gray-400">Exp&eacute;rience des pvtistes</p>
                  </div>
                </div>
                <div className="flex items-center gap-3 p-4 bg-white dark:bg-gray-700/50 rounded-xl border border-gray-100 dark:border-gray-600/50">
                  <div className="w-10 h-10 rounded-xl bg-accent-100 dark:bg-accent-900/30 flex items-center justify-center flex-shrink-0">
                    <Globe2 className="w-5 h-5 text-accent-600 dark:text-accent-400" />
                  </div>
                  <div>
                    <h3 className="font-medium text-gray-900 dark:text-white text-sm">Par pays</h3>
                    <p className="text-xs text-gray-500 dark:text-gray-400">&Eacute;changez par destination</p>
                  </div>
                </div>
              </div>

              <button
                onClick={scrollToTop}
                className="inline-flex items-center gap-2 px-6 py-3 bg-primary-500 hover:bg-primary-600 text-white rounded-xl font-medium shadow-lg shadow-primary-500/20 hover:shadow-xl hover:shadow-primary-500/30 transition-all duration-300 hover:-translate-y-0.5"
              >
                <span>Rejoindre la communaut&eacute;</span>
                <ArrowRight className="w-4 h-4" />
              </button>
            </div>

            <div className="relative lg:h-full min-h-[300px] lg:min-h-0">
              <img
                src="https://images.unsplash.com/photo-1444210971048-6130cf0c46cf?q=80&w=2673&auto=format&fit=crop"
                alt="Groupe de voyageurs"
                className="w-full h-full object-cover"
                loading="lazy"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-black/50 via-transparent to-transparent lg:bg-gradient-to-r lg:from-primary-50/80 dark:lg:from-gray-800/80 lg:via-transparent lg:to-transparent" />

              <div className="absolute inset-0 flex items-center justify-center">
                <div className="bg-white/15 backdrop-blur-md border border-white/25 rounded-2xl p-8 text-center shadow-2xl">
                  <div className="text-4xl sm:text-5xl font-bold text-white mb-2">500k+</div>
                  <div className="text-white/90 text-lg font-medium">membres actifs</div>
                  <div className="mt-4 flex items-center justify-center gap-1">
                    {[...Array(5)].map((_, i) => (
                      <div key={i} className="w-8 h-8 rounded-full bg-gradient-to-br from-white/30 to-white/10 border-2 border-white/40 -ml-2 first:ml-0" />
                    ))}
                    <span className="text-white/80 text-sm ml-2">+499k</span>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
